import React from 'react';
import { AdvancedImage } from '@cloudinary/react';
import { fit, auto, scale } from '@cloudinary/url-gen/actions/resize';
import { cld } from '../../config/cloudinary';

interface OptimizedImageProps {
  publicId: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  aspectRatio?: 'square' | 'landscape' | 'portrait' | 'auto';
  crop?: 'fill' | 'fit' | 'auto';
  loading?: 'lazy' | 'eager';
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  publicId,
  alt,
  width = 800,
  height,
  className = '',
  aspectRatio = 'auto',
  crop = 'auto',
  loading = 'lazy'
}) => {
  // Extract public ID from full Cloudinary URL if needed
  const getPublicId = (url: string): string => {
    if (url.includes('cloudinary.com')) {
      // Extract public ID from full Cloudinary URL
      const parts = url.split('/');
      const uploadIndex = parts.findIndex(part => part === 'upload');
      if (uploadIndex !== -1 && uploadIndex + 2 < parts.length) {
        return parts.slice(uploadIndex + 2).join('/').split('.')[0];
      }
    }
    return url;
  };

  const cleanPublicId = getPublicId(publicId);

  // Build the image transformation
  let img = cld.image(cleanPublicId);

  // Apply format and quality optimizations
  img = img
    .format('auto') // Automatically choose best format (WebP, AVIF, etc.)
    .quality('auto'); // Automatically optimize quality

  // Apply resizing based on aspect ratio and dimensions, using fit to avoid cropping
  if (aspectRatio === 'square') {
    const size = Math.min(width, height || width);
    img = img.resize(fit().width(size).height(size));
  } else if (aspectRatio === 'landscape') {
    img = img.resize(fit().width(width).height(height || Math.round(width * 0.56)));
  } else if (aspectRatio === 'portrait') {
    img = img.resize(fit().width(width).height(height || Math.round(width * 1.4)));
  } else if (crop === 'auto') {
    if (height) {
      img = img.resize(fit().width(width).height(height));
    } else {
      img = img.resize(scale().width(width));
    }
  } else {
    // Manual width/height
    if (height) {
      img = img.resize(fit().width(width).height(height));
    } else {
      img = img.resize(scale().width(width));
    }
  }

  return (
    <AdvancedImage 
      cldImg={img} 
      alt={alt}
      className={className}
      loading={loading}
      style={{ maxWidth: '100%', height: 'auto' }}
    />
  );
};

export default OptimizedImage;
